import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Moment from "moment";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import { rpName } from "storybook-dashboard/dashboard/project/reportStatus";
import { BSBtn } from "traec-react/utils/bootstrap";
import { BreadCrumb } from "AppSrc/project/utils";
import { Spinner } from "traec-react/utils/entities";
import { BasicSpinner } from "storybook-dashboard/spinners/basicSpinner";
import Octicon from "react-octicon";
import { alertSuccess } from "traec-react/utils/sweetalert";
import { useFullIds } from "storybook-dashboard/utils/hooks";
import Im from "immutable";

// Reusable Project Card Component
export const ProjectCard = ({ children, className = "", cardClassName = "mb-4" }) => {
  return (
    <div className="row">
      <div className="col-12">
        <div className={`card ${cardClassName}`}>{children}</div>
      </div>
    </div>
  );
};

// Header Component with title, breadcrumb, and navigation
export const IndicatorHeader = ({ indicatorName, company, project, isRootRef, cref, projectId }) => {
  return (
    <div className="mb-4">
      <h3 className="mb-3">{`Indicator Target: ${indicatorName}`}</h3>
      <BreadCrumb company={company} project={project} isRootRef={isRootRef} cref={cref} />
      <div className="mt-2">
        <Link to={`/project/${projectId}/indicators`} className="btn btn-outline-secondary btn-sm">
          <i className="fa fa-arrow-left mr-2"></i>
          Back to Indicators List
        </Link>
      </div>
    </div>
  );
};

// Target Configuration Component with performance direction and save functionality
export const TargetConfiguration = ({
  greenBelow,
  setGreenBelow,
  onSaveConfiguration,
  onToggleDirection,
  isSaving,
}) => {
  return (
    <ProjectCard>
      <div className="card-header">
        <h5 className="card-title mb-0">
          <Octicon name="settings" style={{ marginRight: "8px" }} />
          Target Configuration
        </h5>
      </div>
      <div className="card-body">
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label font-weight-bold">
              <Octicon name="graph" style={{ marginRight: "6px" }} />
              Performance Direction
            </label>
            <div className="mt-2">
              <p className="text-muted small mb-3">Desired performance above or below the targets and threshold?</p>
              <div className="btn-group" role="group">
                <BSBtn
                  extra_className="d-flex align-items-center"
                  noFloatRight={true}
                  primaryOff={!!greenBelow}
                  text={
                    <span>
                      <Octicon name="arrow-up" style={{ marginRight: "6px" }} />
                      Above Target
                    </span>
                  }
                  onClick={(e) => (onToggleDirection ? onToggleDirection(false) : setGreenBelow(false))}
                  disabled={isSaving}
                />
                <BSBtn
                  extra_className="d-flex align-items-center"
                  noFloatRight={true}
                  primaryOff={!greenBelow}
                  text={
                    <span>
                      <Octicon name="arrow-down" style={{ marginRight: "6px" }} />
                      Below Target
                    </span>
                  }
                  onClick={(e) => (onToggleDirection ? onToggleDirection(true) : setGreenBelow(true))}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>
          <div className="col-md-6 d-flex align-items-end justify-content-end">
            <BSBtn
              extra_className="btn-success"
              noFloatRight={true}
              disabled={isSaving}
              text={
                isSaving ? (
                  <span>
                    <BasicSpinner />
                    <span className="ml-2">Saving...</span>
                  </span>
                ) : (
                  <span>
                    <Octicon name="check" style={{ marginRight: "6px" }} />
                    Save Configuration
                  </span>
                )
              }
              onClick={onSaveConfiguration}
            />
          </div>
        </div>
      </div>
    </ProjectCard>
  );
};

// Table Header Component for consistent table headers
export const TableHeader = () => {
  return (
    <div className="row mb-3 py-2 bg-light rounded">
      <div className="col-md-4">
        <strong>Period / Level</strong>
      </div>
      <div className="col-md-3 text-center">
        <strong>Target Value</strong>
      </div>
      <div className="col-md-3 text-center">
        <strong>Threshold Value</strong>
      </div>
      <div className="col-md-2 text-center">
        <strong>Actions</strong>
      </div>
    </div>
  );
};

// Project Level Target Component for overall project targets input
export const ProjectLevelTarget = ({
  target,
  setTarget,
  threshold,
  setThreshold,
  onDistributeValues,
  onTargetChange,
  onThresholdChange,
}) => {
  return (
    <div className="border-bottom mb-3 pb-3">
      <div className="row align-items-center">
        <div className="col-md-4">
          <div className="d-flex align-items-center">
            <Octicon name="organization" style={{ marginRight: "8px", color: "#007bff" }} />
            <div>
              <strong>Project Level</strong>
              <div className="small text-muted">Overall project targets</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group mb-0">
            <input
              className="form-control text-center"
              type="number"
              step="0.01"
              value={target}
              onChange={(e) => (onTargetChange ? onTargetChange(e.target.value) : setTarget(e.target.value))}
              placeholder="Target value"
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group mb-0">
            <input
              className="form-control text-center"
              type="number"
              step="0.01"
              value={threshold}
              onChange={(e) => (onThresholdChange ? onThresholdChange(e.target.value) : setThreshold(e.target.value))}
              placeholder="Threshold value"
            />
          </div>
        </div>
        <div className="col-md-2 text-center">
          <BSBtn
            extra_className="btn-outline-primary btn-sm"
            onClick={onDistributeValues}
            text={
              <span>
                <Octicon name="git-branch" style={{ marginRight: "4px" }} />
                Distribute
              </span>
            }
            title="Distribute values across all reporting periods"
          />
        </div>
      </div>
    </div>
  );
};

// Reporting Periods Section Component for period-specific targets
export const ReportingPeriodsSection = ({ targets, reportingPeriods, updateReportingPeriod }) => {
  return (
    <div className="mt-4">
      <h6 className="mb-3 text-muted">
        <Octicon name="calendar" style={{ marginRight: "6px" }} />
        Reporting Periods
      </h6>
      <ReportingPeriodRows {...{ targets, reportingPeriods, updateReportingPeriod }} />
    </div>
  );
};

// Target Values Table Component with project level and reporting periods
export const TargetValuesTable = ({
  target,
  setTarget,
  threshold,
  setThreshold,
  targets,
  setTargets,
  reportingPeriods,
  updateReportingPeriod,
  onDistributeValues,
  onTargetChange,
  onThresholdChange,
}) => {
  return (
    <ProjectCard cardClassName="">
      <div className="card-header">
        <h5 className="card-title mb-0">
          <Octicon name="checklist" style={{ marginRight: "8px" }} />
          Target & Threshold Values
        </h5>
      </div>
      <div className="card-body">
        <TableHeader />

        <ProjectLevelTarget
          target={target}
          setTarget={setTarget}
          threshold={threshold}
          setThreshold={setThreshold}
          onDistributeValues={onDistributeValues}
          onTargetChange={onTargetChange}
          onThresholdChange={onThresholdChange}
        />

        <ReportingPeriodsSection
          targets={targets}
          reportingPeriods={reportingPeriods}
          updateReportingPeriod={updateReportingPeriod}
        />
      </div>
    </ProjectCard>
  );
};

const getThresholdFromTarget = (target) => {
  return target.getIn(["meta_json", "thresholdLow"]) || target.getIn(["meta_json", "thresholdHigh"]);
};

function ReportingPeriodRows({ targets, reportingPeriods, updateReportingPeriod }) {
  if (!reportingPeriods) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="text-center">
          <Spinner explanation="Loading Reporting Periods" timedOutComment="Could not load Reporting Periods" />
        </div>
      </div>
    );
  }

  let sortedPeriods = reportingPeriods.sortBy((reportingPeriod) => reportingPeriod.get("startDate")).valueSeq();

  if (sortedPeriods.size === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-muted">
          <Octicon name="calendar" style={{ fontSize: "48px", marginBottom: "16px", display: "block" }} />
          <h6>No Reporting Periods Found</h6>
          <p className="small">Please configure reporting periods for this project first.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {sortedPeriods.map((reportingPeriod, index) => {
        let period = targets.get(reportingPeriod.get("uid"));
        console.log("ReportingPeriodRows", reportingPeriod.get("uid"), targets?.toJS());
        return (
          <ReportingPeriodRow
            key={index}
            reportingPeriod={reportingPeriod}
            target={period?.get("target")}
            threshold={period?.get("threshold")}
            updateReportingPeriod={updateReportingPeriod}
          />
        );
      })}
    </div>
  );
}

const distributeValues = ({ target, threshold, reportingPeriods }) => {
  let num_reportingPeriods = reportingPeriods.size;
  let sortedPeriods = reportingPeriods.sortBy((reportingPeriod) => reportingPeriod.get("startDate"));
  let targets = sortedPeriods.reduce(
    (acc, reportingPeriod) => {
      const periodTarget = target / num_reportingPeriods || 0;
      const periodThreshold = threshold / num_reportingPeriods || 0;

      const runningTarget = acc.runningTarget + periodTarget;
      const runningThreshold = acc.runningThreshold + periodThreshold;

      return {
        targets: acc.targets.set(
          reportingPeriod.get("uid"),
          Im.Map({
            target: runningTarget,
            threshold: runningThreshold,
          })
        ),
        runningTarget,
        runningThreshold,
      };
    },
    { targets: Im.Map(), runningTarget: 0, runningThreshold: 0 }
  );

  return targets.targets;
};

const indicatorLink = (props) => {
  const { projectId, refId, isRootRef } = props;
  if (isRootRef) {
    return `/project/${projectId}/indicators`;
  } else {
    return `/project/${projectId}/wpack/${refId}/indicators`;
  }
};

const updateCumulativeTarget = (e, props) => {
  e.preventDefault();
  const {
    metricTarget,
    baseMetricId,
    target,
    targets,
    greenBelow,
    threshold,
    reportingPeriods,
    trackerId,
    commitId,
    onSaveSuccess,
    onSaveError,
  } = props;

  const metricTargetId = metricTarget?.get("uid");
  const url = `/api/tracker/${trackerId}/commit/${commitId}/target/${metricTargetId ? metricTargetId + "/" : ""}`;
  const method = metricTargetId ? "PATCH" : "POST";

  let targetsByDate = reportingPeriodTargetstoList(targets, reportingPeriods);

  doFetch(url, method, {
    metric: baseMetricId,
    value: target,
    date: Moment(),
    meta_json: {
      byDate: targetsByDate?.toJS(),
      greenBelow: greenBelow,
      thresholdLow: threshold,
    },
  })
    .then(() => {
      if (onSaveSuccess) onSaveSuccess();
      alertSuccess({
        text: `Thank you, the target has been updated`,
        onbgConfirm: () => {
          location.href = indicatorLink(props);
        },
      });
    })
    .catch((error) => {
      console.error("Error saving target configuration:", error);
      if (onSaveError) onSaveError();
    });
};

export default function ProjectIndicatorTarget() {
  const [greenBelow, setGreenBelow] = useState(true);
  const [target, setTarget] = useState(0);
  const [threshold, setThreshold] = useState(0);
  const [targets, setTargets] = useState(Im.Map());
  const [isSaving, setIsSaving] = useState(false);

  const { indicatorId } = useParams();
  const { projectId, trackerId, commitId, refId, cref, isRootRef, company, project } = useFullIds();

  const { data: indicator } = useApi("/api/tracker/{trackerId}/commit/{commitId}/indicator/{indicatorId}/", {
    trackerId,
    commitId,
    indicatorId,
  });
  const { data: metricTargets, trigger: triggerAllTargets } = useApi(
    "/api/tracker/{trackerId}/commit/{commitId}/target/",
    { trackerId, commitId }
  );
  const { data: reportingPeriods } = useApi(`/api/project/{projectId}/reporting_periods/`, { projectId });

  let indicatorName = indicator?.get("name") || "Indicator";
  let baseMetricId = indicator?.getIn(["resultBaseMetric", "uid"]);
  let metricTarget = metricTargets?.find((t) => t.getIn(["metric", "uid"]) === baseMetricId);

  console.log("ProjectIndicatorTarget", indicator?.toJS(), metricTargets?.toJS());

  useEffect(() => {
    if (reportingPeriods && metricTarget && metricTarget.size) {
      console.log("Setting target state from metricTarget", metricTarget.toJS());
      setTarget(metricTarget.get("value"));
      setThreshold(getThresholdFromTarget(metricTarget));
      setTargets(getTargetsFromMetaJson(metricTarget.get("meta_json"), reportingPeriods));
      setGreenBelow(metricTarget.getIn(["meta_json", "greenBelow"]));
    }
  }, [reportingPeriods, metricTarget]);

  const updateReportingPeriod = (reportingPeriod, targetType, value) => {
    let reportingPeriodId = reportingPeriod.get("uid");
    setTargets(targets.updateIn([reportingPeriodId, targetType], (v) => Number(value)));
  };

  // Handle target value changes
  const handleTargetChange = (value) => {
    setTarget(value);
  };

  // Handle threshold value changes with automatic direction adjustment
  const handleThresholdChange = (value) => {
    const thresholdNum = Number(value);
    const targetNum = Number(target);

    // Only auto-adjust if both values are valid numbers and target is not zero
    if (!isNaN(thresholdNum) && !isNaN(targetNum) && targetNum !== 0 && value !== "") {
      // If threshold is greater than target, performance should be "Below Target" (greenBelow = true)
      // If threshold is less than target, performance should be "Above Target" (greenBelow = false)
      if (thresholdNum > targetNum) {
        setGreenBelow(true);
      } else if (thresholdNum < targetNum) {
        setGreenBelow(false);
      }
    }

    setThreshold(value);
  };

  // Handle manual toggle of performance direction
  const handleToggleDirection = (newGreenBelow) => {
    const thresholdNum = Number(threshold);
    const targetNum = Number(target);

    // Check if threshold is set and valid
    if (threshold !== "" && threshold !== 0 && !isNaN(thresholdNum) && !isNaN(targetNum) && targetNum !== 0) {
      // Check if the direction matches the threshold-target relationship
      const thresholdIsAboveTarget = thresholdNum > targetNum;
      const thresholdIsBelowTarget = thresholdNum < targetNum;

      // If switching to "Below Target" (greenBelow = true), threshold must be > target
      // If switching to "Above Target" (greenBelow = false), threshold must be < target
      if (newGreenBelow && thresholdIsBelowTarget) {
        // Invalid: trying to set "Below Target" but threshold is below target
        alert(
          "Performance direction changed to 'Below Target'.\n\n" +
            "The threshold value has been cleared because it was less than the target value.\n\n" +
            "Please enter a threshold value that is greater than the target value."
        );
        setThreshold("");
      } else if (!newGreenBelow && thresholdIsAboveTarget) {
        // Invalid: trying to set "Above Target" but threshold is above target
        alert(
          "Performance direction changed to 'Above Target'.\n\n" +
            "The threshold value has been cleared because it was greater than the target value.\n\n" +
            "Please enter a threshold value that is less than the target value."
        );
        setThreshold("");
      }
    }

    setGreenBelow(newGreenBelow);
  };

  const onSaveSuccess = () => {
    setIsSaving(false);
    triggerAllTargets();
  };

  const onSaveError = () => {
    setIsSaving(false);
  };

  const _props = {
    trackerId,
    commitId,
    projectId,
    refId,
    isRootRef,
    reportingPeriods,
    metricTarget,
    baseMetricId,
    target,
    targets,
    greenBelow,
    threshold,
    triggerAllTargets,
    onSaveSuccess,
    onSaveError,
  };

  // Handler functions for extracted components
  const handleSaveConfiguration = (e) => {
    setIsSaving(true);
    updateCumulativeTarget(e, _props);
  };
  const handleDistributeValues = (e) => setTargets(distributeValues({ target, threshold, reportingPeriods }));

  if (!(indicator && metricTargets)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <BasicSpinner />
          <p className="text-muted mt-3">Loading indicator and target data, please be patient.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <IndicatorHeader
        indicatorName={indicatorName}
        company={company}
        project={project}
        isRootRef={isRootRef}
        cref={cref}
        projectId={projectId}
      />

      <TargetConfiguration
        greenBelow={greenBelow}
        setGreenBelow={setGreenBelow}
        onSaveConfiguration={handleSaveConfiguration}
        onToggleDirection={handleToggleDirection}
        isSaving={isSaving}
      />

      <TargetValuesTable
        target={target}
        setTarget={setTarget}
        threshold={threshold}
        setThreshold={setThreshold}
        targets={targets}
        setTargets={setTargets}
        reportingPeriods={reportingPeriods}
        updateReportingPeriod={updateReportingPeriod}
        onDistributeValues={handleDistributeValues}
        onTargetChange={handleTargetChange}
        onThresholdChange={handleThresholdChange}
      />
    </div>
  );
}

export const ReportingPeriodRow = (props) => {
  let { reportingPeriod, target, threshold, updateReportingPeriod } = props;

  return (
    <div className="row align-items-center py-2 border-bottom">
      <div className="col-md-4">
        <div className="d-flex align-items-center">
          <Octicon name="calendar" style={{ marginRight: "8px", color: "#6c757d" }} />
          <div>
            <strong>{rpName(reportingPeriod)}</strong>
          </div>
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-group mb-0">
          <input
            className="form-control text-center"
            type="number"
            step="0.01"
            value={target?.toFixed(2) || ""}
            onChange={(e) => updateReportingPeriod(reportingPeriod, "target", e.target.value)}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-group mb-0">
          <input
            className="form-control text-center"
            type="number"
            step="0.01"
            value={threshold?.toFixed(2) || ""}
            onChange={(e) => updateReportingPeriod(reportingPeriod, "threshold", e.target.value)}
          />
        </div>
      </div>
      <div className="col-md-2 text-center">
        <small className="text-muted">
          <Octicon name="dash" style={{ marginRight: "4px" }} />
          Period
        </small>
      </div>
    </div>
  );
};

const getReportingPeriodForDate = (dateStr, reportingPeriods) => {
  let date = Moment(dateStr);
  let closest = {
    dt: null,
    period: null,
  };
  for (let reportingPeriod of reportingPeriods) {
    let endDate = Moment(reportingPeriod.get("endDate"));
    let dt = endDate.diff(date, "days");
    if (dt == 0) {
      closest = { dt, period: reportingPeriod };
      break;
    } else if (dt < 0) {
      continue;
    } else if (!closest.dt || (closest.dt && dt < closest.dt)) {
      closest = { dt, period: reportingPeriod };
    }
  }
  return closest.period;
};

export const targetToReportingPeriodMap = (targetDates, reportingPeriods) => {
  return targetDates.reduce((acc, target) => {
    let reportingPeriod = getReportingPeriodForDate(target.get("endDate"), reportingPeriods);
    return reportingPeriod ? acc.set(reportingPeriod.get("uid"), target) : acc;
  }, Im.Map());
};

export const reportingPeriodTargetstoList = (targets, reportingPeriods) => {
  const reportingPeriodsById = reportingPeriods.reduce((acc, rp) => acc.set(rp.get("uid"), rp), Im.Map());
  return targets.entrySeq().reduce((targetList, [key, value]) => {
    let rp = reportingPeriodsById.get(key);
    if (rp) {
      return targetList.push(
        value.merge({
          startDate: rp.get("startDate"),
          endDate: rp.get("endDate"),
        })
      );
    }
    return targetList;
  }, Im.List());
};

export const getTargetsFromMetaJson = (targetMeta, reportingPeriods) => {
  let targets = Im.Map();

  const targetsByDate = targetMeta.get("byDate");
  if (targetsByDate) {
    return targetToReportingPeriodMap(targetsByDate, reportingPeriods.toList());
  }

  for (let key of targetMeta.keySeq()) {
    if (key.match(/[0-9a-f-]{36}/g)) {
      targets = targets.set(key, targetMeta.get(key));
    }
  }

  return targets;
};

export const getMetricTarget = (state, baseMetricId, commitId) => {
  let metricTargets = state.getInPath(`entities.commitEdges.byId.${commitId}.metricTargets`);
  try {
    let metricTarget = metricTargets.filter((target) => {
      if (target.getIn(["metric", "uid"]) === baseMetricId) {
        return target;
      }
    });
    return metricTarget.first();
  } catch (e) {
    return Im.Map();
  }
};
